import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-consent',
  templateUrl: './consent.component.html',
  styleUrls: ['./consent.component.css']
})
export class ConsentComponent implements OnInit {
  showBanner = false;
  showSettingsPanel = false;

  performanceCookies = false;
  functionalCookies = false;
  targetingCookies = false;

  expandedSection: string | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  toggleSection(section: string): void {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const consent = this.getCookie('ICLS_CC');
      if (!consent) {
        setTimeout(() => {
          this.showBanner = true;
        }, 500);
      } else if (consent === 'granted' || consent === 'customized' || consent === 'true') {
        const savedPrefs = this.getCookie('ICLS_CC_PREFS');
        if (savedPrefs) {
          try {
            const parsed = JSON.parse(decodeURIComponent(savedPrefs));
            this.performanceCookies = !!parsed.performance;
            this.functionalCookies = !!parsed.functional;
            this.targetingCookies = !!parsed.targeting;
          } catch {
            this.performanceCookies = true;
            this.functionalCookies = true;
            this.targetingCookies = true;
          }
        } else {
          this.performanceCookies = true;
          this.functionalCookies = true;
          this.targetingCookies = true;
        }
        this.applyConsentSignals(this.performanceCookies, this.targetingCookies, this.functionalCookies);
      } else {
        // User previously declined
        this.applyConsentSignals(false, false, false);
      }
    }
  }

  acceptCookies(): void {
    this.performanceCookies = true;
    this.functionalCookies = true;
    this.targetingCookies = true;

    this.setCookie('ICLS_CC', 'granted', 365);
    this.setCookie('ICLS_CC_PREFS', JSON.stringify({ performance: true, functional: true, targeting: true }), 365);
    this.applyConsentSignals(true, true, true);

    this.showBanner = false;
    this.showSettingsPanel = false;
  }

  declineCookies(): void {
    this.performanceCookies = false;
    this.functionalCookies = false;
    this.targetingCookies = false;

    this.setCookie('ICLS_CC', 'false', 365);
    this.setCookie('ICLS_CC_PREFS', JSON.stringify({ performance: false, functional: false, targeting: false }), 365);
    this.applyConsentSignals(false, false, false);

    this.showBanner = false;
    this.showSettingsPanel = false;
  }

  showCustomSettings(): void {
    this.showSettingsPanel = true;
  }

  hideCustomSettings(): void {
    this.showSettingsPanel = false;
  }

  confirmSelection(): void {
    const isAnyAllowed = this.performanceCookies || this.targetingCookies || this.functionalCookies;
    this.setCookie('ICLS_CC', isAnyAllowed ? 'customized' : 'false', 365);
    this.setCookie(
      'ICLS_CC_PREFS',
      JSON.stringify({
        performance: this.performanceCookies,
        functional: this.functionalCookies,
        targeting: this.targetingCookies
      }),
      365
    );

    this.applyConsentSignals(this.performanceCookies, this.targetingCookies, this.functionalCookies);
    this.showSettingsPanel = false;
    this.showBanner = false;
  }

  private applyConsentSignals(analyticsGranted: boolean, marketingGranted: boolean, functionalGranted: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const w = window as any;
    if (typeof w.gtag === 'function') {
      w.gtag('consent', 'update', {
        analytics_storage: analyticsGranted ? 'granted' : 'denied',
        functionality_storage: functionalGranted ? 'granted' : 'denied',
        ad_storage: marketingGranted ? 'granted' : 'denied',
        ad_user_data: marketingGranted ? 'granted' : 'denied',
        ad_personalization: marketingGranted ? 'granted' : 'denied'
      });
    }

    if (typeof w.clarity === 'function') {
      try {
        w.clarity('consent', analyticsGranted);
      } catch {
        // Safe catch if clarity API signature varies
      }
    }
  }

  private setCookie(name: string, value: string | boolean, days: number): void {
    if (isPlatformBrowser(this.platformId)) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      const expires = `expires=${date.toUTCString()}`;
      document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
    }
  }

  private getCookie(name: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const nameEQ = `${name}=`;
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i].trim();
        if (c.indexOf(nameEQ) === 0) {
          return decodeURIComponent(c.substring(nameEQ.length));
        }
      }
    }
    return null;
  }

  private deleteCookie(name: string): void {
    if (isPlatformBrowser(this.platformId)) {
      document.cookie = `${name}=; Max-Age=-99999999; path=/;`;
    }
  }
}
